import { Feather } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { TopMenu, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import SwitchCompany from '_/components/TopMenu/SwitchCompany';
import { appConfig } from '_/config/app';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { usePushNotificationContext } from '_/hooks/PushNotificationProvider';
import { AppRoute, ProfileProps } from '_/routes/types';
import AllowedHostsDB from '_/services/sqlite/AllowedHostsDB';
import UsersZonesDB from '_/services/sqlite/UsersZonesDB';
import { signOut } from '_/store/modules/auth/actions';
import * as MailComposer from 'expo-mail-composer';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  Platform,
  Alert,
  Image,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import * as SecureStore from 'expo-secure-store';
import * as LocalAuthentication from 'expo-local-authentication';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import SwitchLanguage from './SwitchLanguage';
import { VERSION } from '_/app-version';

export default function Profile() {
  const navigation = useNavigation<ProfileProps>();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const user = useSelector((state: any) => state.user);
  const company = useSelector((state: any) => state.company);
  const { token, loading, checkNotificationsPermissions, removeTokenFromDatabase } =
    usePushNotificationContext();

  const [offlineQRCodesEnabled, setOfflineQRCodesEnabled] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    loadStatus();
  }, [token]);

  const [biometricsAvailable, setBiometricsAvailable] = useState(false);
  const [biometricsEnabled, setBiometricsEnabled] = useState(false);

  useEffect(() => {
    async function loadBiometricsStatus() {
      const biometricsStatus = await SecureStore.getItemAsync('biometrics_status');

      setBiometricsAvailable(biometricsStatus === 'available' || biometricsStatus === 'enabled');

      setBiometricsEnabled(biometricsStatus === 'enabled');
    }

    loadBiometricsStatus();
  }, []);

  const toggleBiometrics = async () => {
    if (biometricsEnabled) {
      await SecureStore.deleteItemAsync('user_email');
      await SecureStore.deleteItemAsync('user_password');
      await SecureStore.deleteItemAsync('biometrics_status');

      setBiometricsEnabled(false);
    } else {
      const savedEmail = await SecureStore.getItemAsync('user_email');
      const savedPassword = await SecureStore.getItemAsync('user_password');

      if (!savedEmail && !savedPassword) {
        Alert.alert(
          translate('biometrics.title'),
          `${translate('biometrics.reloginToEnable')}\n\n${translate('biometrics.securityNotice')}`
        );
      } else {
        await SecureStore.setItemAsync('biometrics_status', 'enabled');
        setBiometricsEnabled(true);
      }
    }
  };

  useEffect(() => {
    async function loadOfflineQRConfig() {
      const storedValue = await SecureStore.getItemAsync('offline_qrcodes_enabled');
      setOfflineQRCodesEnabled(storedValue === 'true');
    }

    loadOfflineQRConfig();
  }, []);

  async function loadStatus() {
    const storedStatus = await AsyncStorage.getItem('@w3lcome/notifications');

    if (storedStatus) {
      setStatus(storedStatus);
    }
  }

  const handleLogout = async () => {
    const isInitialized = await AsyncStorage.getItem('@w3lcome/dbInitialized');

    if (isInitialized && Platform.OS !== 'web') {
      try {
        await UsersZonesDB?.deleteTable();
        await AllowedHostsDB?.deleteTable();
        await AsyncStorage.removeItem('@w3lcome/dbInitialized');
      } catch (error) {
        console.log(error);
      }
    }

    await removeTokenFromDatabase();
    await AsyncStorage.getAllKeys()
      .then((keys) => {
        AsyncStorage.multiRemove(keys);
      })
      .catch(() => null);
    dispatch(signOut());
  };

  const onNotificationPress = useCallback(async () => {
    if (token) {
      await removeTokenFromDatabase();
    } else {
      await checkNotificationsPermissions();
    }
  }, [token]);

  const toggleOfflineQRCodes = async () => {
    const newValue = !offlineQRCodesEnabled;

    await SecureStore.setItemAsync('offline_qrcodes_enabled', newValue ? 'true' : 'false');

    setOfflineQRCodesEnabled(newValue);
  };

  const handleEditProfile = useCallback(() => {
    navigation.navigate(AppRoute.EDIT_PROFILE);
  }, []);

  const renderProfileHeader = useCallback(() => {
    return (
      <View style={styles.header}>
        <View style={styles.avatarContainer}>
          <Image
            source={user.pictureUrl ? { uri: user.pictureUrl } : themes.images.avatarSquare}
            style={styles.avatar}
          />
        </View>

        <Text style={styles.title}>{user.name}</Text>
        <Text style={styles.subTitle}>{user.email}</Text>
        {!!user.position && <Text style={styles.subTitle}>{user.position}</Text>}
      </View>
    );
  }, [user]);

  const renderNotifications = useCallback(() => {
    // TODO: para testar tirei o  {token && status !== 'denied' && !loading && (
    return (
      <>
        {token && !loading && (
          <Text style={[styles.notificationStatus, { color: themes.colors.success }]}>
            ({translate('profile.ENABLED')})
          </Text>
        )}
        {!token && status !== 'denied' && !loading && (
          <Text style={[styles.notificationStatus, { color: themes.colors.danger }]}>
            ({translate('profile.DISABLED')})
          </Text>
        )}
        {status === 'denied' && !loading && (
          <Text style={[styles.notificationStatus, { color: themes.colors.danger }]}>
            ({translate('profile.DENIED')})
          </Text>
        )}
        {loading && (
          <ActivityIndicator size="small" color={themes.colors.primary} style={{ marginLeft: 4 }} />
        )}
      </>
    );
  }, [token, loading, translate, status]);

  const renderSubMenu = useCallback(
    (
      title: string,
      subTitle: string,
      icon = 'chevron-right',
      withNotification = false,
      iconColor?: string
    ) => {
      return (
        <View style={styles.inputGroup}>
          <View style={styles.inputGroupContent}>
            <Text style={[styles.menuTitle, icon === 'log-out' && { color: themes.colors.danger }]}>
              {title}
            </Text>
            {withNotification && renderNotifications()}
            <Text style={styles.menuSubTitle}>{subTitle}</Text>
          </View>
          <Feather
            name={icon as any}
            style={[styles.menuIcon, iconColor && { color: iconColor }]}
          />
        </View>
      );
    },
    [renderNotifications]
  );

  if (!user) return <View />;

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <TopMenu />
        <ScrollView showsVerticalScrollIndicator={false}>
          {renderProfileHeader()}
          <View style={styles.content}>
            {/* ================= PERFIL ================= */}
            <Text style={styles.sectionTitle}>{translate('generalSettings')}</Text>
            <View style={styles.sectionCard}>
              <TouchableOpacity onPress={handleEditProfile}>
                {renderSubMenu(
                  translate('profile.settingsTitle'),
                  translate('profile.settingsSubTitle')
                )}
              </TouchableOpacity>

              <TouchableOpacity onPress={onNotificationPress}>
                {renderSubMenu(
                  translate('profile.notificationsTitle'),
                  translate('profile.notificationsSubTitle'),
                  token ? 'toggle-right' : 'toggle-left',
                  true,
                  token ? themes.colors.success : undefined
                )}
              </TouchableOpacity>
              {/* <TouchableOpacity onPress={toggleOfflineQRCodes}>
                {renderSubMenu(
                  'QR Codes Offline',
                  offlineQRCodesEnabled
                    ? 'Visualização offline habilitada'
                    : 'Permite visualizar QR Codes sem internet',
                  offlineQRCodesEnabled ? 'toggle-right' : 'toggle-left',
                  false,
                  offlineQRCodesEnabled ? themes.colors.success : undefined
                )}
              </TouchableOpacity> */}
            </View>

            {/* ================= SEGURANÇA ================= */}
            <Text style={styles.sectionTitle}>{translate('securityPrivacy')}</Text>
            <View style={styles.sectionCard}>
              {biometricsAvailable && (
                <TouchableOpacity onPress={toggleBiometrics}>
                  {renderSubMenu(
                    translate('biometrics.title'),
                    biometricsEnabled
                      ? translate('biometrics.menuDescriptionEnabled')
                      : translate('biometrics.menuDescriptionDisabled'),
                    biometricsEnabled ? 'toggle-right' : 'toggle-left',
                    false,
                    biometricsEnabled ? themes.colors.success : undefined
                  )}
                </TouchableOpacity>
              )}

              <TouchableOpacity onPress={() => navigation.navigate(AppRoute.DELETE_ACCOUNT)}>
                {renderSubMenu(
                  translate('profile.deleteAccountTitle'),
                  translate('profile.deleteAccountSubTitle')
                )}
              </TouchableOpacity>
            </View>

            {/* ================= CONTA ================= */}
            <Text style={styles.sectionTitle}>{translate('account')}</Text>
            <View style={styles.sectionCard}>
              <SwitchLanguage />
              <SwitchCompany
                content={renderSubMenu(
                  translate('profile.switchProfileTitle'),
                  translate('profile.switchProfileSubTitle')
                )}
              />

              <TouchableOpacity onPress={() => setLogoutModalVisible(true)}>
                {renderSubMenu(
                  translate('profile.logoutTitle'),
                  translate('profile.logoutSubTitle'),
                  'log-out'
                )}
              </TouchableOpacity>
            </View>

            {/* ================= SUPORTE ================= */}
            <Text style={styles.sectionTitle}>{translate('support')}</Text>
            <View style={styles.sectionCard}>
              <TouchableOpacity
                onPress={() =>
                  MailComposer.composeAsync({
                    recipients: [appConfig.helpEmail],
                    subject: translate('profile.help', {
                      AppName: appConfig.appName,
                      CompanyName: company.name,
                      HostEmail: user.email,
                    }),
                  })
                }
              >
                {renderSubMenu(translate('profile.needHelp'), translate('contactSupport'))}
              </TouchableOpacity>
            </View>

            {!!VERSION && <Text style={styles.version}>{`v. ${VERSION}`}</Text>}
          </View>
        </ScrollView>

        <LogoutConfirmationModal
          handleLogout={async () => await handleLogout()}
          visible={logoutModalVisible}
          hideModal={() => setLogoutModalVisible(false)}
        />
      </View>
    </ContainerWidthLimit>
  );
}

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.lightQuaternary,
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  sectionCard: {
    backgroundColor: themes.colors.white,
    borderRadius: 16,
    paddingHorizontal: 14,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: fontFamily.medium,
    color: themes.colors.grayPrimary,
    marginBottom: 8,
    marginLeft: 4,
  },
  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    paddingHorizontal: 24,
    backgroundColor: themes.colors.white,
    borderBottomLeftRadius: 48,
    borderBottomRightRadius: 48,
  },
  avatarContainer: {
    marginBottom: 16,
  },
  avatar: {
    width: 110,
    height: 110,
    borderRadius: 55,
    resizeMode: 'cover',
  },
  title: {
    color: themes.colors.grayPrimary,
    fontFamily: fontFamily.bold,
    fontSize: 22,
  },
  subTitle: {
    color: themes.colors.grayPrimary,
    fontSize: 14,
  },
  inputGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  inputGroupContent: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginRight: 12,
  },
  menuTitle: {
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.medium,
    fontSize: 16,
    marginBottom: 4,
  },
  menuSubTitle: {
    color: themes.colors.grayPrimary,
    fontSize: 12,
    marginBottom: 8,
  },
  menuIcon: {
    marginLeft: 12,
    fontSize: 18,
    color: themes.colors.lightSecondary,
  },
  notificationStatus: {
    fontFamily: fontFamily.medium,
    fontSize: 10,
    lineHeight: 10,
    paddingBottom: 8,
  },
  version: {
    textAlign: 'center',
    color: themes.colors.grayPrimary,
    fontSize: 12,
    marginTop: 12,
  },
  loadingView: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
  },
});
